import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
/**
 * The selected tag's own rules (Tag.rulesDiscussionId), shown directly in
 * the "Nouvelle discussion" composer rather than behind a link opening
 * TagRulesModal (see showTagRulesInComposer.js's own docblock for why it
 * was a modal in the first place, and why this reverts that - requested
 * directly, twice: once for a plain phpBB-style forum, then champis-forum
 * migrated to Flarum and this became a link+modal specifically because an
 * *earlier* inline version let the composer's own fixed height (300px,
 * core's Composer.less) clip a long rules post instead of scrolling it).
 *
 * That clipping risk is why -body below gets its own bounded max-height +
 * overflow-y: auto (see champis-theme's own forum.less) instead of
 * growing freely: capping how much space this can ever take keeps the
 * composer's own total height predictable regardless of how long the
 * rules post is, which is the one thing that broke the original inline
 * version - "prend de la place" (real layout space, shrinking the editor
 * below it, not an overlay) but only ever a bounded amount of it,
 * requested directly as "le composer doit garder sa taille".
 *
 * onbeforeupdate() below resets `closed`/`discussion` and re-fetches
 * whenever a different tag (a different rulesDiscussionId) gets picked -
 * same pattern as SpeciesGallery.js's own gbifId-change handling. This
 * used to rely on `key={discussionId}` at the call site instead (letting
 * Mithril tear down and remount a keyed component when its key changes,
 * for a fresh oninit with no explicit comparison needed) - confirmed
 * directly that this doesn't reliably reset when the tag actually changes
 * in a real composer ("les règles ne sont pas modifiées"): Mithril's own
 * keyed-diff algorithm needs *every* sibling in the same children array to
 * carry a key to behave consistently, and the other three
 * Composer.view() children this gets pushed alongside (Composer-handle,
 * Composer-controls, Composer-content) have none - mixing a lone keyed
 * vnode in among unkeyed siblings is a known Mithril footgun, not
 * something to fight with more keys.
 *
 * Positioned (see champis-theme's own forum.less) with `bottom: 100%`
 * against .Composer, which sits at the *bottom* of the viewport on
 * desktop (core's Composer.less, `@media @tablet-up { .App-composer {
 * position: fixed; bottom: 0; ...} }`) - "above" the composer there just
 * means "further from the bottom edge", plenty of viewport room. On
 * phone, though, .Composer is pinned to `top: 0` instead (a full-width
 * panel starting at the very top of the screen) - "above" it would be
 * *off the top of the screen entirely*, confirmed directly (a negative
 * getBoundingClientRect().top). --champis-tag-rules-notice-height (read
 * by champis-theme's own `@media @phone { .Composer:not(.minimized) {
 * top: var(...) } }` override) is how this reserves real room instead:
 * self-measured here on every render and written to that custom
 * property, so .Composer's own top offset grows to match, and this ends
 * up filling exactly the gap that opens up above it - the mobile
 * equivalent of pushing the editor down that desktop gets from the
 * fixed-bottom layout for free. Reset to 0 wherever this can stop taking
 * up room: closing it explicitly, or Mithril tearing the whole component
 * down (tag changed, composer closed).
 */
const RESERVED_HEIGHT_PROPERTY = '--champis-tag-rules-notice-height';
export default class TagRulesNotice extends Component {
  discussion = null;
  closed = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.discussion = null;
    this.closed = false;
    this.load(vnode.attrs.discussionId);
  }
  onbeforeupdate(vnode) {
    // Compared BEFORE calling super, not after: core's own
    // Component.onbeforeupdate() immediately overwrites this.attrs with
    // vnode.attrs (setAttrs()) - calling super first, then comparing
    // vnode.attrs against this.attrs, always compares a value against
    // itself and never detects a change. Confirmed directly - this is
    // exactly why the first version of this fix didn't actually work
    // ("les règles ne sont pas modifiées" persisted even after adding
    // this method, until the order was swapped).
    if (vnode.attrs.discussionId !== this.attrs.discussionId) {
      this.discussion = null;
      this.closed = false;
      this.load(vnode.attrs.discussionId);
    }
    super.onbeforeupdate(vnode);
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.updateReservedHeight(vnode);
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.updateReservedHeight(vnode);
  }
  onremove(vnode) {
    super.onremove(vnode);
    document.documentElement.style.setProperty(RESERVED_HEIGHT_PROPERTY, '0px');
  }

  // +8 matches .TagRulesNotice's own margin-bottom (champis-theme's forum.less)
  // - getBoundingClientRect() only ever measures the border box, not margins,
  // and the reserved gap needs to include that spacing too or the notice
  // would sit flush against the composer's title bar on phone.
  updateReservedHeight(vnode) {
    const height = vnode.dom ? vnode.dom.getBoundingClientRect().height + 8 : 0;
    document.documentElement.style.setProperty(RESERVED_HEIGHT_PROPERTY, `${height}px`);
  }
  view() {
    if (this.closed) return null;
    if (!this.discussion) {
      return m("div", {
        className: "TagRulesNotice"
      }, m(LoadingIndicator, {
        size: "small"
      }));
    }
    const post = this.discussion.firstPost();
    if (!post) {
      document.documentElement.style.setProperty(RESERVED_HEIGHT_PROPERTY, '0px');
      return null;
    }
    return m("div", {
      className: "TagRulesNotice"
    }, m("div", {
      className: "TagRulesNotice-header"
    }, m("span", {
      className: "TagRulesNotice-title"
    }, m("i", {
      className: "icon fas fa-circle-info",
      "aria-hidden": "true"
    }), this.discussion.title()), m("button", {
      type: "button",
      className: "Button Button--icon Button--flat TagRulesNotice-close",
      "aria-label": app.translator.trans('champis-forum.forum.tag_rules.close_button'),
      onclick: () => {
        this.closed = true;
        // view() returns null from here on, so oncreate/onupdate
        // (the normal place this var gets set) never fire again for
        // this instance - clear it immediately instead of leaving
        // phone's .Composer permanently offset by a notice that's
        // no longer there.
        document.documentElement.style.setProperty(RESERVED_HEIGHT_PROPERTY, '0px');
      }
    }, m("i", {
      className: "icon fas fa-times",
      "aria-hidden": "true"
    }))), m("div", {
      className: "TagRulesNotice-body Post-body"
    }, m.trust(post.contentHtml())));
  }
  load(discussionId) {
    app.store.find('discussions', String(discussionId), {
      include: 'firstPost'
    }).then(discussion => {
      this.discussion = discussion;
      m.redraw();
    }).catch(() => {
      // Deleted/inaccessible rules discussion - fail quiet rather than
      // sit on a spinner forever, same reasoning as the modal version
      // this replaces.
      this.closed = true;
      m.redraw();
    });
  }
}
flarum.reg.add('champis-forum', 'forum/components/TagRulesNotice', TagRulesNotice);